import * as Tone from "tone";
import type { PatternParts } from "~/types/pattern";
import type { SupportedSynth } from "~/lib/audio/ccMapping";
import { startToneIfSuspended } from "./audioContext";
import { EnvelopeGate } from "./EnvelopeGate";

// Normalize note name for Tone.js ("F♯" → "F#", "E♭" → "Eb")
function normalizeNoteName(note: string): string {
  return note.replace("♯", "#").replace("♭", "b");
}

export type NoteGeneratorConfig = {
  bpm: number;
  swing: number; // -100 to +100, where 0 is no swing
  pattern: string[]; // e.g. ["A", "B", "A", "B"]
  patternParts: PatternParts; // e.g. { A: [...], B: [...] }
};

export type NoteGeneratorCallbacks = {
  onNotePlayed?: (note: string, partIndex: number, noteIndexInPart: number) => void;
  onRest?: (partIndex: number, noteIndexInPart: number) => void;
  onPartChange?: (partName: string, partIndex: number) => void;
  onPatternComplete?: () => void; // Called when pattern completes a full cycle
  shouldPlaySynth?: () => boolean; // Called to check if internal synth should play
  onBeforeNotePlay?: (note: string) => void; // Called before note triggers - use for keyboard tracking
};

/**
 * NoteGenerator - Plays pre-generated pattern parts sequentially
 *
 * This class manages playback of pattern parts (A, B, C, etc.),
 * looping through the pattern sequence indefinitely.
 *
 * Uses Tone.js Transport for precise timing that works even when
 * the browser tab is in the background.
 */
export class NoteGenerator {
  private synth: SupportedSynth;
  private isRunning = false;
  private config: NoteGeneratorConfig;
  private callbacks: NoteGeneratorCallbacks;
  private loop: Tone.Loop | null = null;
  private envelopeGate: EnvelopeGate;

  // Pattern playback state
  private currentPatternIndex = 0; // Position in pattern sequence (e.g., 0-3 for ABAB)
  private currentNoteIndex = 0; // Position within current part

  constructor(synth: SupportedSynth, config: NoteGeneratorConfig, callbacks: NoteGeneratorCallbacks = {}, envelopeGate?: EnvelopeGate) {
    this.synth = synth;
    this.config = config;
    this.callbacks = callbacks;
    this.envelopeGate = envelopeGate ?? new EnvelopeGate();

    // Configure Tone.js Transport BPM
    Tone.getTransport().bpm.value = config.bpm;
  }

  /**
   * Start the note generation loop
   * NOTE: Does NOT start Tone.Transport - caller must start Transport after all devices are ready
   */
  async start() {
    if (this.isRunning) return;

    this.isRunning = true;
    this.currentPatternIndex = 0;
    this.currentNoteIndex = 0;

    console.log(
      `[START] BPM: ${this.config.bpm}, Pattern: ${this.config.pattern.join("")}`
    );

    // iOS fix: Only call Tone.start() if context is suspended
    // The context should already be running (resumed in toggleStart)
    // Calling Tone.start() in async context breaks user gesture chain on iOS
    await startToneIfSuspended();

    // Create a Tone.Loop that calls our playback function
    // The loop will determine its own interval based on what we return
    this.loop = new Tone.Loop((time) => {
      this.playNote(time);
    });

    // Start the loop (but NOT the Transport - that's controlled externally)
    this.loop.start(0);
  }

  /**
   * Stop the note generation loop
   * NOTE: Does NOT stop Tone.Transport - caller must stop Transport after all devices are stopped
   */
  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;

    // Cancel all scheduled events on the synth to prevent notes from playing after stop
    this.synth.unsync();
    this.synth.triggerRelease();

    // Stop and dispose the loop
    if (this.loop) {
      this.loop.stop();
      this.loop.dispose();
      this.loop = null;
    }

    console.log("[STOP] Stopped playback");
  }

  /**
   * Update the configuration and reset to start
   * NOTE: Does NOT restart Transport - caller must handle Transport control
   */
  updateConfig(config: Partial<NoteGeneratorConfig>) {
    const wasRunning = this.isRunning;

    // Stop current playback
    if (wasRunning) {
      // Cancel all scheduled events on the synth
      this.synth.unsync();
      this.synth.triggerRelease();

      if (this.loop) {
        this.loop.stop();
        this.loop.dispose();
        this.loop = null;
      }
    }

    // Update config
    this.config = { ...this.config, ...config };

    // Update Transport BPM if changed
    if (config.bpm !== undefined) {
      Tone.getTransport().bpm.value = config.bpm;
    }

    // Reset to start of pattern when config changes
    this.resetPosition();

    // Restart playback if it was running
    if (wasRunning) {
      this.loop = new Tone.Loop((time) => {
        this.playNote(time);
      });
      this.loop.start(0);
    }
  }

  /**
   * Update the configuration without resetting position (for seamless pattern switching)
   */
  updateConfigContinuous(config: Partial<NoteGeneratorConfig>) {
    // Just update the config and BPM - keep playing from current position
    this.config = { ...this.config, ...config };

    // Update Transport BPM if changed
    if (config.bpm !== undefined) {
      Tone.getTransport().bpm.value = config.bpm;
    }
  }

  /**
   * Update callbacks (useful for updating shouldPlaySynth when devices change)
   */
  updateCallbacks(callbacks: Partial<NoteGeneratorCallbacks>) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Reset playback position to start of pattern
   */
  resetPosition() {
    this.currentPatternIndex = 0;
    this.currentNoteIndex = 0;
  }

  /**
   * Set playback position to a specific part and beat within that part
   * This is used for beat-synchronized pattern scrambling
   *
   * @param partIndex - Index in the pattern sequence (0-based)
   * @param beatWithinPart - Beat index within the part (0-based, where 0 is first beat)
   */
  setPosition(partIndex: number, beatWithinPart: number) {
    // Validate partIndex
    if (partIndex < 0 || partIndex >= this.config.pattern.length) {
      console.warn(`[WARN] Invalid partIndex ${partIndex}, pattern length: ${this.config.pattern.length}`);
      return;
    }

    const partName = this.config.pattern[partIndex];
    const part = this.config.patternParts[partName as keyof PatternParts];

    if (!part || part.length === 0) {
      console.warn(`[WARN] Part "${partName}" not found or empty`);
      return;
    }

    // Calculate noteIndex from beatWithinPart
    // Count durations until we reach the target beat
    // For now, assume uniform note duration within a part for simplicity
    // This will work correctly for patterns with consistent note lengths
    // More complex patterns with varying durations would need duration-aware calculation

    // Simple approach: map beat to note proportionally
    // If we have 8 notes and 4 beats, each beat = 2 notes
    // beatWithinPart 0 → noteIndex 0
    // beatWithinPart 1 → noteIndex 2
    // beatWithinPart 2 → noteIndex 4
    // beatWithinPart 3 → noteIndex 6

    // For now, use a simple linear mapping based on typical eighth note patterns
    // TODO: Make this duration-aware by calculating actual beat positions
    const notesPerBeat = part.length / 4; // Assuming 4/4 time (beatsPerBar = 4)
    const noteIndex = Math.floor(beatWithinPart * notesPerBeat);

    // Clamp to valid range
    const clampedNoteIndex = Math.max(0, Math.min(noteIndex, part.length - 1));

    this.currentPatternIndex = partIndex;
    this.currentNoteIndex = clampedNoteIndex;

    console.log(`🎯 [SET POSITION] Part ${partIndex} (${partName}), beat ${beatWithinPart} → note ${clampedNoteIndex}/${part.length}`);
  }

  /**
   * Check if the generator is currently running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Get current pattern position (0-based index)
   */
  get patternPosition(): number {
    return this.currentPatternIndex;
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.stop();
  }

  /**
   * Play the current note and advance to the next
   * This is called by Tone.Loop at the appropriate time
   */
  private playNote(time: number) {
    // Get current part name (A, B, C, etc.)
    const partName = this.config.pattern[this.currentPatternIndex];
    const part = this.config.patternParts[partName as keyof PatternParts];

    if (!part || part.length === 0) {
      console.warn(`[WARN] Part "${partName}" not found or empty, skipping`);
      this.advancePattern();
      // Update loop interval to quarter note and return
      if (this.loop) {
        this.loop.interval = "4n";
      }
      return;
    }

    // Notify if we just started a new part
    if (this.currentNoteIndex === 0) {
      console.log(`[PART ${partName}] Starting part at pattern position ${this.currentPatternIndex}`);
      this.callbacks.onPartChange?.(partName, this.currentPatternIndex);
    }

    // Get current note from part
    const patternNote = part[this.currentNoteIndex];

    // Apply swing timing: shift off-beat notes (odd indices: 1, 3, 5, 7...)
    // Swing value ranges from -100 (early) to +100 (late)
    let swingOffset = 0;
    if (this.config.swing !== 0 && this.currentNoteIndex % 2 === 1) {
      // This is an off-beat note - apply swing
      // Calculate the time shift as a percentage of half the note duration
      const noteDuration = Tone.Time(patternNote.duration).toSeconds();
      // Swing percentage: -100 to +100 becomes -0.5 to +0.5 of the note duration
      // This creates the classic "shuffle" feel where off-beats are delayed or anticipated
      swingOffset = (this.config.swing / 100) * (noteDuration * 0.5);
    }

    // Calculate which beat we're on in the overall pattern
    // Count total notes from all previous parts + current position
    let totalBeatsSoFar = 0;
    for (let i = 0; i < this.currentPatternIndex; i++) {
      const previousPartName = this.config.pattern[i];
      const previousPart = this.config.patternParts[previousPartName as keyof typeof this.config.patternParts];
      if (previousPart) {
        totalBeatsSoFar += previousPart.length;
      }
    }
    const currentBeat = totalBeatsSoFar + this.currentNoteIndex + 1;

    if (patternNote.note === null) {
      // Rest
      console.log(`[BEAT ${currentBeat}] REST in Part ${partName} (note ${this.currentNoteIndex + 1}/${part.length})`);
      this.callbacks.onRest?.(this.currentPatternIndex, this.currentNoteIndex);
    } else {
      // Play note (note already includes octave number from PatternGenerator)
      const toneNote = normalizeNoteName(patternNote.note);
      console.log(`[BEAT ${currentBeat}] ${toneNote} in Part ${partName} (note ${this.currentNoteIndex + 1}/${part.length})`);

      // Play the note using the synth at the scheduled time (only if shouldPlaySynth callback allows)
      const shouldPlay = this.callbacks.shouldPlaySynth?.() ?? true; // Default to true if not provided

      // Use EnvelopeGate to calculate proper note duration with anti-click gaps
      // This replaces the hardcoded 0.9 multiplier with configurable envelope timing
      const originalDurationSeconds = Tone.Time(patternNote.duration).toSeconds();
      const shortenedDuration = this.envelopeGate.getEffectiveDuration(originalDurationSeconds);

      // Apply swing offset to the scheduled time
      const swungTime = time + swingOffset;

      console.log(`🔊 [TONE.JS] shouldPlay: ${shouldPlay}, time: ${swungTime.toFixed(3)}s (swing: ${swingOffset.toFixed(3)}s), note: ${toneNote}, duration: ${shortenedDuration.toFixed(3)}s (${patternNote.duration})`);
      if (shouldPlay) {
        // Apply keyboard tracking (filter adjustment based on note pitch) before triggering
        this.callbacks.onBeforeNotePlay?.(toneNote);
        this.synth.triggerAttackRelease(toneNote, shortenedDuration, swungTime);
        console.log(`🎹 [TONE.JS PLAYED] ${toneNote} at ${swungTime.toFixed(3)}s`);
      }

      // Notify callback
      console.log(`📢 [CALLBACK] onNotePlayed for beat ${currentBeat}, partIndex: ${this.currentPatternIndex}, noteIndex: ${this.currentNoteIndex}`);
      this.callbacks.onNotePlayed?.(toneNote, this.currentPatternIndex, this.currentNoteIndex);
    }

    // Update the loop interval to match the current note's duration
    if (this.loop) {
      this.loop.interval = patternNote.duration;
    }

    // Advance to next note
    this.currentNoteIndex++;

    // Check if we've reached the end of this part
    if (this.currentNoteIndex >= part.length) {
      this.currentNoteIndex = 0;
      this.advancePattern();
    }
  }

  /**
   * Advance to the next part in the pattern sequence
   */
  private advancePattern() {
    const nextIndex = this.currentPatternIndex + 1;

    // Check if we're wrapping around to the start
    if (nextIndex >= this.config.pattern.length) {
      this.currentPatternIndex = 0;
      // Notify that the pattern has completed a full cycle
      this.callbacks.onPatternComplete?.();
    } else {
      this.currentPatternIndex = nextIndex;
    }
  }

  /**
   * Calculate the total length of the pattern in beats
   */
  getTotalPatternLength(): number {
    let total = 0;
    for (const partName of this.config.pattern) {
      const part = this.config.patternParts[partName as keyof PatternParts];
      if (part) {
        total += part.length;
      }
    }
    return total;
  }

  /**
   * Get the current global beat position within the pattern (0-based)
   */
  getCurrentBeatPosition(): number {
    let totalBeats = 0;
    // Count beats from all previous parts
    for (let i = 0; i < this.currentPatternIndex; i++) {
      const partName = this.config.pattern[i];
      const part = this.config.patternParts[partName as keyof PatternParts];
      if (part) {
        totalBeats += part.length;
      }
    }
    // Add current note index
    return totalBeats + this.currentNoteIndex;
  }
}
